import { useState, useCallback, useMemo } from 'react';

/** 兌換購物車邏輯 hook：草稿數量與點數預算計算，選品頁可編輯，確認頁與列表頁唯讀。 */
const useRedeemCart = (products = [], availablePoints = 0) => {
    const [cart, setCart] = useState({}); // { [itemID]: quantity }

    const usedPoints = useMemo(
        () => products.reduce((sum, p) => sum + p.points * (cart[p.itemID] || 0), 0),
        [products, cart],
    );

    const totalItems = useMemo(() => Object.values(cart).reduce((sum, qty) => sum + qty, 0), [cart]);
    const hasCartItems = totalItems > 0;
    const isOverBudget = usedPoints > availablePoints;
    // 可用上限 − 本次已使用
    const remainingPoints = availablePoints - usedPoints;

    const canAddItem = useCallback(
        (points) => usedPoints + points <= availablePoints,
        [usedPoints, availablePoints],
    );

    // 還有貨可兌，但剩餘點數已不夠換任何一項
    const isPointsInsufficient = useMemo(() => {
        const inStock = products.filter((p) => p.isEnabled);
        return inStock.length > 0 && !inStock.some((p) => canAddItem(p.points));
    }, [products, canAddItem]);

    const addItem = useCallback(
        (itemID, points) => {
            if (!canAddItem(points)) return;
            setCart((prev) => ({ ...prev, [itemID]: (prev[itemID] || 0) + 1 }));
        },
        [canAddItem],
    );

    // 減一件，歸零時刪掉 key
    const removeItem = useCallback((itemID) => {
        setCart((prev) => {
            const nextQty = (prev[itemID] || 0) - 1;
            if (nextQty <= 0) {
                const next = { ...prev };
                delete next[itemID];
                return next;
            }
            return { ...prev, [itemID]: nextQty };
        });
    }, []);

    // 以外部 cart 覆蓋目前狀態
    const resetCart = useCallback((newCart = {}) => setCart({ ...newCart }), []);

    return {
        cart,
        usedPoints,
        remainingPoints,
        totalItems,
        hasCartItems,
        isOverBudget,
        isPointsInsufficient,
        canAddItem,
        addItem,
        removeItem,
        resetCart,
    };
};

export default useRedeemCart;
